import React from "react";
import { queryClient } from "../../../../index";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getGrafanaData,
  deleteGrafanaToken,
  createGrafanaToken,
  GrafanaDataType,
} from "./GrafanaFunctions";
import { message, Spin } from "antd";
import styles from "./styles.module.css";

// Define the component props type that will be enhanced
type GrafanaComponentProps = {
  createToken: () => void;
  isUpdating: boolean;
  data: GrafanaDataType | undefined;
  deleteToken: () => void;
};

// The enhanced component props
type EnhancedComponentProps = {
  accountId: string;
};

function GrafanaHOC<P extends object>(
  Component: React.ComponentType<GrafanaComponentProps>
) {
  // Create a proper function component that follows React Hooks rules
  const EnhancedComponent: React.FC<EnhancedComponentProps> = ({
    accountId,
  }) => {
    // Hooks are now at the top level of a function component
    const { data, isFetched } = useQuery({
      queryKey: ["grafana_data", { accountId: accountId }],
      queryFn: () => getGrafanaData(accountId),
      enabled: !!accountId,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      staleTime: 300000,
    });

    const { mutate: createToken, isPending } = useMutation<
      GrafanaDataType, // Success response type
      Error, // Error type
      void, // Variables type
      GrafanaDataType // Context type
    >({
      mutationFn: () =>
        createGrafanaToken(accountId)
          .then((res) => res)
          .catch((err) => {
            message.error(err.message);
            throw err;
          }),
      onSuccess: (newData) => {
        queryClient.setQueryData<GrafanaDataType>(
          ["grafana_data", { accountId: accountId }],
          () => {
            return newData;
          }
        );
      },
    });

    const { mutate: deleteToken, isPending: isDeleting } = useMutation<
      boolean,
      Error,
      void,
      boolean
    >({
      mutationFn: () => deleteGrafanaToken(accountId),
      onError: (err) => {
        message.error(err.message);
        throw err;
      },
      onSuccess: () => {
        queryClient.setQueryData<GrafanaDataType>(
          ["grafana_data", { accountId: accountId }],
          () => {
            return {};
          }
        );
      },
    });

    if (isFetched && data) {
      return (
        <Component
          createToken={createToken}
          isUpdating={isPending || isDeleting}
          data={data}
          deleteToken={deleteToken}
        />
      );
    } else {
      return (
        <div className={styles.container}>
          <Spin />
        </div>
      );
    }
  };

  return EnhancedComponent;
}

export default GrafanaHOC;
